import React, { useMemo } from "react";
import {
  Box,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import { observer } from "mobx-react";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import AppTableHeaderCell from "../../../../../views/components/common/pages/AppTableHeaderCell";
import AppTableRowCell from "../../../../../views/components/common/pages/AppTableRowCell";
import { OrderProductTatInsight } from "../../../../../models/Order/OrderProductTatInsight";
import { formatDateWithoutTime } from "../../../../../views/components/common/functions/formatDate";
import {
  StyledTableCell,
  StyledTableRow,
} from "../../../../../styles/Shipmentstyles";

interface OrderProductTatInsightsPopupProps {
  open: boolean;
  insights: OrderProductTatInsight[];
  onClose: () => void;
}

type ColumnKey = keyof OrderProductTatInsight;

interface InsightColumn {
  key: ColumnKey;
  label: string;
  minWidth: string;
  align: "left" | "center" | "right";
  suffix?: string;
}

const ALL_COLUMNS: InsightColumn[] = [
  {
    key: "name",
    label: "Product",
    minWidth: "140px",
    align: "left",
  },
  {
    key: "product_id",
    label: "ID",
    minWidth: "52px",
    align: "center",
  },
  { key: "order_qty", label: "Qty", minWidth: "44px", align: "center" },
  { key: "timeline", label: "Timeline", minWidth: "72px", align: "center" },
  { key: "buffer", label: "Buffer", minWidth: "64px", align: "center" },
  {
    key: "pr_tat_from_project",
    label: "PR TAT (project)",
    minWidth: "64px",
    align: "center",
    suffix: "d",
  },
  {
    key: "raw_tat_from_pr",
    label: "Raw (PR)",
    minWidth: "72px",
    align: "center",
    suffix: "d",
  },
  {
    key: "customised_tat_from_pr",
    label: "Custom (PR)",
    minWidth: "88px",
    align: "center",
    suffix: "d",
  },
  {
    key: "raw_tat_from_project",
    label: "Raw TAT (project)",
    minWidth: "72px",
    align: "center",
    suffix: "d",
  },
  {
    key: "customised_tat_from_project",
    label: "Custom TAT (project)",
    minWidth: "88px",
    align: "center",
    suffix: "d",
  },
  {
    key: "avg_raw_rejection_pct",
    label: "Avg Rej %",
    minWidth: "80px",
    align: "center",
    suffix: "%",
  },
  {
    key: "overall_raw_rejection_pct",
    label: "Overall Rej %",
    minWidth: "96px",
    align: "center",
    suffix: "%",
  },
];

const hasValue = (value: unknown): boolean =>
  value !== null && value !== undefined && value !== "";

const formatCell = (
  value: unknown,
  suffix?: string,
): string => {
  if (!hasValue(value)) return "—";
  return suffix ? `${value}${suffix}` : String(value);
};

const OrderProductTatInsightsPopup: React.FC<
  OrderProductTatInsightsPopupProps
> = ({ open, insights, onClose }) => {
  const theme = useTheme();

  const orderSummary = useMemo(() => {
    if (!insights.length) return null;
    const first = insights[0];
    return {
      order_id: first.order_id,
      order_type: first.order_type,
      brand_name: first.brand_name,
      dispatch_date: first.dispatch_date,
    };
  }, [insights]);


  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: theme.palette.customColors.backdrop,
            backdropFilter: "blur(4px)",
          },
        },
        paper: {
          sx: {
            borderRadius: "16px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.15)",
            overflow: "hidden",
            maxHeight: "92vh",
            margin: { xs: "12px", sm: "20px" },
            background: theme.palette.customColors.white,
          },
        },
      }}
      sx={{ zIndex: 2000 }}
    >
      <Box
        sx={{
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "4px",
            background: `linear-gradient(90deg, ${theme.palette.customColors.blueGradientStart} 0%, ${theme.palette.customColors.blueGradientEnd} 50%, ${theme.palette.customColors.blueGradientStart} 100%)`,
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 1,
            px: 2.5,
            py: 1.75,
            borderBottom: `1px solid ${theme.palette.customColors.greyBorder}`,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}>
            <Box
              sx={{
                width: 34,
                height: 34,
                borderRadius: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: theme.palette.primary.main + "14",
                color: theme.palette.primary.main,
                mt: 0.25,
              }}
            >
              <TimelineOutlinedIcon sx={{ fontSize: 18 }} />
            </Box>
            <Box>
              <CustomTypography variant="heading5" fontWeight={700}>
                Product TAT Insights
              </CustomTypography>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 0.75,
                  mt: 0.75,
                }}
              >
                {orderSummary && (
                  <Chip
                    size="small"
                    label={`Order #${orderSummary.order_id}`}
                    sx={{ height: 22, fontSize: "0.72rem", fontWeight: 600 }}
                  />
                )}
                {orderSummary?.order_type && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={orderSummary.order_type}
                    sx={{ height: 22, fontSize: "0.72rem" }}
                  />
                )}
                {orderSummary?.brand_name && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={orderSummary.brand_name}
                    sx={{ height: 22, fontSize: "0.72rem" }}
                  />
                )}
                {orderSummary?.dispatch_date && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Dispatch ${formatDateWithoutTime(orderSummary.dispatch_date)}`}
                    sx={{ height: 22, fontSize: "0.72rem" }}
                  />
                )}
                {insights.length > 0 && (
                  <Chip
                    size="small"
                    label={`${insights.length} product${insights.length > 1 ? "s" : ""}`}
                    sx={{
                      height: 22,
                      fontSize: "0.72rem",
                      backgroundColor: theme.palette.primary.main + "12",
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                    }}
                  />
                )}
              </Box>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={{ p: 0, overflow: "hidden" }}>
          {insights.length === 0 ? (
            <Box sx={{ py: 5, textAlign: "center" }}>
              <CustomTypography variant="body2" color="text.secondary">
                No product TAT insights found for this order.
              </CustomTypography>
            </Box>
          ) : (
            <TableContainer
              sx={{
                maxHeight: "min(520px, calc(92vh - 160px))",
                overflow: "auto",
                borderTop: `1px solid ${theme.palette.customColors.greyBorder}`,
              }}
            >
              <Table size="small" stickyHeader sx={{ minWidth: 640 }}>
                <TableHead>
                  <TableRow>
                    {ALL_COLUMNS.map((col, index) => (
                      <AppTableHeaderCell
                        key={col.key}
                        title={col.label}
                        minWidth={col.minWidth}
                        txtAlign={col.align}
                        sx={{
                          py: 1,
                          px: 1.25,
                          fontSize: "0.72rem",
                          position: "sticky",
                          top: 0,
                          zIndex: 2,
                          backgroundColor:
                            theme.palette.customColors.lightGreyColor ||
                            "#f8fafc",
                          ...(index === 0 && {
                            left: 0,
                            zIndex: 3,
                            boxShadow: "2px 0 4px rgba(0,0,0,0.04)",
                          }),
                        }}
                      />
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {insights.map((row, rowIndex) => (
                    <StyledTableRow
                      key={row.product_id}
                      sx={{
                        backgroundColor:
                          rowIndex % 2 === 0
                            ? theme.palette.background.paper
                            : theme.palette.customColors.lightGreyColor + "80",
                      }}
                    >
                      {ALL_COLUMNS.map((col, colIndex) => {
                        const raw = row[col.key];

                        if (colIndex === 0) {
                          return (
                            <StyledTableCell
                              key={col.key}
                              align={col.align}
                              sx={{
                                py: 0.75,
                                px: 1.25,
                                fontSize: "0.8rem",
                                fontWeight: 600,
                                maxWidth: 200,
                                position: "sticky",
                                left: 0,
                                zIndex: 1,
                                backgroundColor: "inherit",
                                boxShadow: "2px 0 4px rgba(0,0,0,0.04)",
                              }}
                            >
                              <CustomTypography
                                variant="body2"
                                fontWeight={600}
                                sx={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: 180,
                                }}
                              >
                                {formatCell(raw)}
                              </CustomTypography>
                            </StyledTableCell>
                          );
                        }

                        return (
                          <AppTableRowCell
                            key={col.key}
                            minWidth={col.minWidth}
                            sx={{
                              py: 0.75,
                              px: 1.25,
                              fontSize: "0.8rem",
                              textAlign: col.align,
                              fontWeight:
                                col.key === "buffer" ||
                                  col.key.includes("rejection")
                                  ? 600
                                  : 400,
                            }}
                          >
                            {formatCell(raw, col.suffix)}
                          </AppTableRowCell>
                        );
                      })}
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Box>
    </Dialog>
  );
};

export default observer(OrderProductTatInsightsPopup);
